"use client";

import { FormEvent, useRef, useState } from "react";
import { authService } from "@/services/auth";
import ErrorMessage from "@/components/ui/ErrorMessage";

interface SalesforceLinkFormProps {
  onSkip: () => void;
  onSuccess: () => void;
}

export default function SalesforceLinkForm({ onSkip, onSuccess }: SalesforceLinkFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const submitLock = useRef(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    setIsLoading(true);
    setError("");
    const fd = new FormData(e.currentTarget);
    const email = fd.get("email") as string;
    const password = fd.get("password") as string;
    try {
      await authService.linkSalesforce(email.trim(), password);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao vincular Salesforce");
    } finally {
      setIsLoading(false);
      submitLock.current = false;
    }
  };

  return (
    <div
      className={`relative w-full max-w-md rounded-xl bg-white p-6 shadow-lg sm:p-8 ${isLoading ? "pointer-events-none" : ""}`}
    >
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">
          Você possui um usuário no Salesforce?
        </h1>
      </div>

      {isLoading && (
        <div
          className="pointer-events-none absolute inset-0 z-10 rounded-xl bg-white/60 backdrop-blur-[2px] transition-opacity"
          aria-hidden
        />
      )}

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} />
        </div>
      )}

      <form method="post" action="/login" onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            E-mail Salesforce
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            disabled={isLoading}
            autoComplete="email"
            placeholder="seu@email.com"
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm transition-all focus:border-amber-primary/50 focus:outline-none focus:ring-2 focus:ring-amber-primary/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
            Senha Salesforce
          </label>
          <input
            type="password"
            id="password"
            name="password"
            required
            disabled={isLoading}
            autoComplete="current-password"
            placeholder="••••••••"
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm transition-all focus:border-amber-primary/50 focus:outline-none focus:ring-2 focus:ring-amber-primary/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onSkip}
            disabled={isLoading}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            NÃO TENHO
          </button>
          <button
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
            className="relative flex min-h-[42px] flex-1 items-center justify-center gap-2 rounded-lg bg-amber-cta py-2.5 text-sm font-semibold text-white transition-all hover:bg-amber-ctaHover disabled:cursor-not-allowed disabled:opacity-90"
          >
            {isLoading && (
              <span
                className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/35 border-t-white"
                aria-hidden
              />
            )}
            <span>{isLoading ? "Vinculando…" : "ACESSAR"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
